import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addWeeks, endOfWeek, parseISO, startOfWeek } from 'date-fns';
import { observer } from 'mobx-react';
import { Box, Button, Center, Flex, Loader, Stack, Tabs } from '@mantine/core';
import { DatePicker, DatePickerProps } from '@mantine/dates';
import dayjs from 'dayjs';

import useExternalApiService from '../../../helpers/ExternalApiService';
import { store } from 'stores/Store';

import { ReactComponent as AddRouteIcon } from 'assets/addRoute.svg';
import { ReactComponent as CheckInIcon } from "assets/check_in.svg";
import { ReactComponent as ListBulletIcon } from 'assets/ListBullets.svg';
import { ReactComponent as CalendarIcon } from 'assets/calendar.svg';

import styles from './FollowUpsTab.module.scss';

interface Props {
  onClickPractitioner: (practitioner: any) => void;
  onClickPractitionerCheckin: (practitioner: any) => void;
  onPractitionerAddedToRoute: () => void;
}

const dayRenderer: DatePickerProps['renderDay'] = (date) => {
  const today = dayjs();
  const day = dayjs(date);
  return (
    <div className={day < today ? styles.dateBefore : ''}>{day.date()}</div>
  );
};

const FollowUpsTab = observer(({ onClickPractitioner, onClickPractitionerCheckin, onPractitionerAddedToRoute }: Props) => {
  const { postData } = useExternalApiService(process.env.REACT_APP_EAI_URL);

  const selectedDate = store.selectedFollowUpDate;
  const practitioners = store.practitionersInFollowUpDate;
  const followUpPractitionersUpdateFlag = store.followUpPractitionersUpdateFlag;

  const [selectedTab, setSelectedTab] = useState<'list_current' | 'list_past' | 'calendar'>('list_current');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [submitting, setSubmitting] = useState(false);

  const submitAbortControllerRef = useRef<AbortController | null>(null);

  const [practitionersThisWeek, practitionersFollowingWeek, practitionersTwoOrMoreWeeks] = useMemo(() => {
    const today = new Date();

    // Considers weeks starting on Monday
    const startOfThisWeek = startOfWeek(today, { weekStartsOn: 1 });
    const endOfThisWeek = endOfWeek(today, { weekStartsOn: 1 });
    const startOfFollowingWeek = selectedTab === 'list_current' ? startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 }) : startOfWeek(addWeeks(today, -1), { weekStartsOn: 1 });
    const endOfFollowingWeek = selectedTab === 'list_current' ? endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 }) : endOfWeek(addWeeks(today, -1), { weekStartsOn: 1 });

    const practitionersThisWeek: { [date: string]: any[] } = {};
    const practitionersFollowingWeek: { [date: string]: any[] } = {};
    const practitionersTwoOrMoreWeeks: { [date: string]: any[] } = {};

    practitioners.forEach((practitioner) => {
      const followupDateStr = practitioner.next_followup_date;

      let followupDate: Date;
      try {
        followupDate = parseISO(followupDateStr);
      } catch {
        followupDate = new Date(followupDateStr);
      }

      // This week (Monday to Sunday)
      if (followupDate >= startOfThisWeek && followupDate <= endOfThisWeek) {
        if (!(followupDateStr in practitionersThisWeek)) {
          practitionersThisWeek[followupDateStr] = [];
        }

        practitionersThisWeek[followupDateStr].push(practitioner);
        return;
      }

      if (followupDate >= startOfFollowingWeek && followupDate <= endOfFollowingWeek) {
        if (!(followupDateStr in practitionersFollowingWeek)) {
          practitionersFollowingWeek[followupDateStr] = [];
        }

        practitionersFollowingWeek[followupDateStr].push(practitioner);
        return;
      }

      // 2 or more weeks away
      if (!(followupDateStr in practitionersTwoOrMoreWeeks)) {
        practitionersTwoOrMoreWeeks[followupDateStr] = [];
      }
      practitionersTwoOrMoreWeeks[followupDateStr].push(practitioner);
    });

    return [practitionersThisWeek, practitionersFollowingWeek, practitionersTwoOrMoreWeeks];
  }, [practitioners, selectedTab]);

  const fetchFollowUpsOnSelectedDate = useCallback(async () => {
    setLoading(true);

    const abortController = new AbortController();
    submitAbortControllerRef.current = abortController;

    try {
      const response = await postData(
        '/api/get_practitioners_for_chat',
        {
          fmc_email: store.impersonatedUser,
          page: 1,
          limit: 50,
          alias: store.impersonatedUserAlias,
          'follow_up_date': selectedDate,
        },
        abortController.signal,
      );

      store.setPractitionersInFollowUpDate(response?.practitioners ?? []);
      setLoading(false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }

      setLoading(false);
      console.error('Error fetching follow-ups:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, followUpPractitionersUpdateFlag]);

  const fetchFollowUpList = useCallback(async () => {
    setLoading(true);

    const abortController = new AbortController();
    submitAbortControllerRef.current = abortController;

    try {
      const response = await postData(
        '/api/get_practitioners_for_chat_followup',
        {
          fmc_email: store.impersonatedUser,
          page,
          limit: 100,
          order: selectedTab === 'list_current' ? 'asc' : 'desc',
        },
        abortController.signal,
      );

      store.setPractitionersInFollowUpDate(response?.practitioners ?? []);
      setLoading(false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }

      setLoading(false);
      console.error('Error fetching follow-ups:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedTab, followUpPractitionersUpdateFlag]);

  const handleCustomDateChange = (val: string | null) => {
    store.setFollowUpDateSearch(val);
    store.setPractitionersInFollowUpDate([]);
  };

  const handleCompleteFollowUp = (practitionerId: number) => async () => {
    await postData(`/api/practitioner/${practitionerId}/followup/complete`);

    if (selectedTab === 'calendar') {
      fetchFollowUpsOnSelectedDate();
    } else {
      fetchFollowUpList();
    }
  };

  const renderPractitionerRow = (practitioner: any) => {
    return (
      <Flex
        key={practitioner.id}
        mb={10}
        gap={5}
        className={styles.followUpItem}
        justify="space-between"
        align="center"
      >
        <Flex align="center" gap={{ base: 5, sm: 10 }}>
          <Button
            type="button"
            color="#58E487"
            disabled={submitting}
            loading={submitting}
            classNames={{ root: styles.doneButton }}
            onClick={handleCompleteFollowUp(practitioner.id)}
          >
            Done
          </Button>
          <div onClick={() => onClickPractitioner(practitioner)} style={{ cursor: 'pointer' }}>
            <div className={styles.practitionerName}>{practitioner.name}</div>
            <div className={styles.address}>{practitioner.addresses && practitioner.addresses.length > 0 ? practitioner.addresses[0].address : 'No address available'}</div>
          </div>
        </Flex>
        <Flex align="center" gap={5} flex="0 0 auto">
          <Button
            type="button"
            variant="transparent"
            disabled={submitting}
            loading={submitting}
            classNames={{ root: styles.actionButton, label: styles.actionButtonLabel }}
            onClick={() => onClickPractitionerCheckin(practitioner)}
          >
            <CheckInIcon stroke="#4F7D9C" width={25} height={25} />
            <div>Check-In</div>
          </Button>
          <Button
            type="button"
            variant="transparent"
            disabled={submitting}
            loading={submitting}
            classNames={{ root: styles.actionButton, label: styles.actionButtonLabel }}
            onClick={() => {
              store.addToRoute(practitioner);
              onPractitionerAddedToRoute();
            }}
          >
            <AddRouteIcon height={25} />
            <div>Add to Route</div>
          </Button>
        </Flex>
      </Flex>
    );
  }

  useEffect(() => {
    if (!selectedDate || selectedTab !== 'calendar') {
      return;
    }

    fetchFollowUpsOnSelectedDate();

    return () => {
      submitAbortControllerRef.current?.abort();
      submitAbortControllerRef.current = null;
      setLoading(false);
    };
  }, [fetchFollowUpsOnSelectedDate, selectedDate, selectedTab]);

  useEffect(() => {
    if (selectedTab === 'calendar') {
      return;
    }

    fetchFollowUpList();

    return () => {
      submitAbortControllerRef.current?.abort();
      submitAbortControllerRef.current = null;
      setLoading(false);
    };
  }, [fetchFollowUpList, selectedTab, page]);

  return (
    <Box py={10}>
      <Tabs
        value={selectedTab}
        onChange={(val) => setSelectedTab(val as 'list_current' | 'list_past' | 'calendar')}
        classNames={{
          list: styles.tabs,
          tab: styles.tabButton,
        }}
      >
        <Tabs.List
          style={{
            justifyContent: selectedTab === 'calendar' ? 'flex-end' : 'space-between'
          }}
        >
          {(selectedTab === 'list_current' || selectedTab === 'list_past') && (
            <div className={styles.tabsWrapper}>
              <Tabs.Tab
                value="list_current"
                className={styles.leftTab}
              >
                Current
              </Tabs.Tab>
              <Tabs.Tab
                value="list_past"
                className={styles.leftTab}
              >
                Past Due
              </Tabs.Tab>
            </div>
          )}

          <div className={styles.tabsWrapper}>
            <Tabs.Tab
              value="list_current"
              leftSection={(
                <ListBulletIcon
                  width={18}
                  height={18}
                />
              )}
              className={`${styles.rightTab}${selectedTab === 'list_past' ? ` ${styles.activeTab}` : ''}`}
            />
            <Tabs.Tab
              value="calendar"
              leftSection={(
                <CalendarIcon
                  width={18}
                  height={18}
                />
              )}
              className={styles.rightTab}
            />
          </div>
        </Tabs.List>

        <Tabs.Panel value="list_current">
          {loading ? (
              <Center style={{ height: '200px' }}>
                <Loader size="lg" />
              </Center>
            ) : (
              <Stack py={20} gap={30}>
                <div>
                  <div className={styles.weekHeader}>This Week</div>
                  {Object.keys(practitionersThisWeek).length === 0 ? (
                    <div className={styles.noFollowUps}>No follow-ups this week.</div>
                  ) : (
                    Object.keys(practitionersThisWeek).sort().map((dateStr) => (
                      <div key={dateStr}>
                        <div className={styles.dateHeader}>{dayjs(dateStr).format('MMMM D, YYYY')}</div>
                        {practitionersThisWeek[dateStr].map(renderPractitionerRow)}
                      </div>
                    ))
                  )}
                </div>
                <div>
                  <div className={styles.weekHeader}>Next Week</div>
                  {Object.keys(practitionersFollowingWeek).length === 0 ? (
                    <div className={styles.noFollowUps}>No follow-ups next week.</div>
                  ) : (
                    <Stack gap={10}>
                      {Object.keys(practitionersFollowingWeek).sort().map((dateStr) => (
                        <div key={dateStr}>
                          <div className={styles.dateHeader}>{dayjs(dateStr).format('MMMM D, YYYY')}</div>
                          {practitionersFollowingWeek[dateStr].map(renderPractitionerRow)}
                        </div>
                      ))}
                    </Stack>
                  )}
                </div>
                <div>
                  <div className={styles.weekHeader}>+2 Weeks Out</div>
                  {Object.keys(practitionersTwoOrMoreWeeks).length === 0 ? (
                    <div className={styles.noFollowUps}>No follow-ups 2+ weeks out.</div>
                  ) : (
                    <Stack gap={10}>
                      {Object.keys(practitionersTwoOrMoreWeeks).sort().map((dateStr) => (
                        <div key={dateStr}>
                          <div className={styles.dateHeader}>{dayjs(dateStr).format('MMMM D, YYYY')}</div>
                          {practitionersTwoOrMoreWeeks[dateStr].map(renderPractitionerRow)}
                        </div>
                      ))}
                    </Stack>
                  )}
                </div>
              </Stack>
            )}
        </Tabs.Panel>

        <Tabs.Panel value="list_past">
          {loading ? (
              <Center style={{ height: '200px' }}>
                <Loader size="lg" />
              </Center>
            ) : (
              <Stack py={20} gap={30}>
                <div>
                  <div className={styles.weekHeader}>This Week</div>
                  {Object.keys(practitionersThisWeek).length === 0 ? (
                    <div className={styles.noFollowUps}>No past due follow-ups this week.</div>
                  ) : (
                    Object.keys(practitionersThisWeek).sort().map((dateStr) => (
                      <div key={dateStr}>
                        <div className={styles.dateHeader}>{dayjs(dateStr).format('MMMM D, YYYY')}</div>
                        {practitionersThisWeek[dateStr].map(renderPractitionerRow)}
                      </div>
                    ))
                  )}
                </div>
                <div>
                  <div className={styles.weekHeader}>Last Week</div>
                  {Object.keys(practitionersFollowingWeek).length === 0 ? (
                    <div className={styles.noFollowUps}>No past due follow-ups last week.</div>
                  ) : (
                    <Stack gap={10}>
                      {Object.keys(practitionersFollowingWeek).sort().map((dateStr) => (
                        <div key={dateStr}>
                          <div className={styles.dateHeader}>{dayjs(dateStr).format('MMMM D, YYYY')}</div>
                          {practitionersFollowingWeek[dateStr].map(renderPractitionerRow)}
                        </div>
                      ))}
                    </Stack>
                  )}
                </div>
                <div>
                  <div className={styles.weekHeader}>More than 2 Weeks Overdue</div>
                  {Object.keys(practitionersTwoOrMoreWeeks).length === 0 ? (
                    <div className={styles.noFollowUps}>No past due follow-ups 2+ weeks out.</div>
                  ) : (
                    <Stack gap={10}>
                      {Object.keys(practitionersTwoOrMoreWeeks).sort().map((dateStr) => (
                        <div key={dateStr}>
                          <div className={styles.dateHeader}>{dayjs(dateStr).format('MMMM D, YYYY')}</div>
                          {practitionersTwoOrMoreWeeks[dateStr].map(renderPractitionerRow)}
                        </div>
                      ))}
                    </Stack>
                  )}
                </div>
              </Stack>
            )}
        </Tabs.Panel>

        <Tabs.Panel value="calendar">
          <>
            <Flex justify="center" p={10}>
              <DatePicker
                value={selectedDate}
                onChange={handleCustomDateChange}
                hideOutsideDates
                firstDayOfWeek={0}
                renderDay={dayRenderer}
                classNames={{
                  day: styles.calendarDay
                }}
              />
            </Flex>
            {loading ? (
              <Center style={{ height: '200px' }}>
                <Loader size="lg" />
              </Center>
            ) : (
              practitioners.length === 0 ? (
                <Center style={{ height: '200px' }}>
                  No follow-ups scheduled for the selected date.
                </Center>
              ) : (
                practitioners.map(renderPractitionerRow)
              )
            )}
          </>
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
});

export default FollowUpsTab;
